import React from 'react';

import type { HubItemKind } from '../api/types';
import { ExternalLink } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { BackLink, GroupHeading } from '../components/Chrome';
import ContentLoader from '../components/ContentLoader';
import ContentMessage from '../components/ContentMessage';
import HubItemRow from '../components/HubItemRow';
import ShadowContent from '../components/ShadowContent';
import { useHubArchive } from '../hooks/useHubArchive';
import { useHubItem } from '../hooks/useHubItem';
import { buildArticleArchivePath, buildNewsArchivePath } from '../utils/contentPaths';

const COPY = {
  article: {
    backLabel: 'All articles',
    notFoundTitle: 'Article not found',
    notFoundBody: 'The article you are looking for doesn’t exist or isn’t available.',
    errorBody: 'This article couldn’t be loaded. Please try again later.',
    empty: 'This article has no content.',
    externalLead: 'This article is published on another site.',
    externalCta: 'Read it there',
    relatedFallback: 'More articles',
  },
  news: {
    backLabel: 'All news',
    notFoundTitle: 'News item not found',
    notFoundBody: 'The news item you are looking for doesn’t exist or isn’t available.',
    errorBody: 'This news item couldn’t be loaded. Please try again later.',
    empty: 'This news item has no content.',
    externalLead: 'This item is published on another site.',
    externalCta: 'Read it there',
    relatedFallback: 'More news',
  },
} as const;

// How many related entries sit under a finished piece. Enough to offer somewhere to go
// next, few enough that the page still ends.
const RELATED_COUNT = 3;

const formatDate = (iso: string): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

interface RelatedItemsProps {
  kind: HubItemKind;
  /** The piece being read, so it can be kept out of its own related list. */
  currentId: string;
  categoryId: string;
  categoryTitle: string;
}

/**
 * Where to go after the last paragraph.
 *
 * A separate component rather than a hook on the page, so the fetch is issued once with the
 * category already known instead of firing bare and refetching when the item lands.
 *
 * The rows are `HubItemRow` — the same entry the archives use, deliberately. A related list
 * with a row shape of its own would be a second listing pattern for one idea, which is how a
 * design system comes apart. Nothing renders unless there is something to show: no heading
 * over an empty region, and no "no related items" note, which is a sentence that tells the
 * reader nothing they wanted.
 */
const RelatedItems: React.FC<RelatedItemsProps> = ({
  kind,
  currentId,
  categoryId,
  categoryTitle,
}) => {
  // One more than needed: the piece being read is in its own category and comes back with
  // the rest, so it has to be dropped without leaving the list short.
  const { items, state } = useHubArchive(kind, RELATED_COUNT + 1, categoryId);

  if (state !== 'ready') {
    return null;
  }

  const related = items.filter((item) => item.id !== currentId).slice(0, RELATED_COUNT);
  if (related.length === 0) {
    return null;
  }

  return (
    <section className="mt-10 border-t border-rule-strong pt-8">
      <GroupHeading>
        {categoryTitle ? `More in ${categoryTitle}` : COPY[kind].relatedFallback}
      </GroupHeading>
      <ul className="divide-y divide-rule border-b border-rule">
        {related.map((item) => (
          <li key={item.id}>
            <HubItemRow item={item} />
          </li>
        ))}
      </ul>
    </section>
  );
};

interface HubItemPageProps {
  kind: HubItemKind;
}

// The reading view for one article or news item — the same shape as the newsletter detail
// page, which is the hub's established form for "one dated thing, read start to finish".
const HubItemPage: React.FC<HubItemPageProps> = ({ kind }) => {
  const params = useParams();
  const { item, html, state } = useHubItem(kind, params['idOrSlug'] ?? '');
  const copy = COPY[kind];
  const archivePath = kind === 'article' ? buildArticleArchivePath() : buildNewsArchivePath();

  if (state === 'loading') {
    return <ContentLoader />;
  }

  if (state === 'notfound') {
    return <ContentMessage title={copy.notFoundTitle} body={copy.notFoundBody} />;
  }

  if (state === 'error') {
    return <ContentMessage title="Something went wrong" body={copy.errorBody} />;
  }

  const meta = [formatDate(item?.date ?? ''), item?.categoryTitle].filter(Boolean).join(' · ');

  return (
    <article className="mx-auto max-w-measure py-12 sm:py-16">
      <BackLink to={archivePath} label={copy.backLabel} />

      <header className="border-b border-rule-strong pb-6">
        <h1 className="font-display text-step-3 leading-[1.05] tracking-[-0.02em] text-ink">
          {item?.title}
        </h1>
        {meta && <p className="mt-3 font-ui text-step--1 text-ink-muted">{meta}</p>}
      </header>

      {item?.pictureUrl && (
        // A plate window: the photograph is cut into the sheet behind a hairline, not floated
        // on it with a radius. No shadow, and no rounding beyond the system's single 2px.
        <figure className="mt-8 border border-rule-strong">
          <img src={item.pictureUrl} alt="" className="max-h-96 w-full object-cover" />
        </figure>
      )}

      <div className="mt-8">
        {item?.externalUrl ? (
          // Filed here, hosted elsewhere: the item still gets a row and a page in the hub, and
          // this is where the reader is handed off.
          <div className="border-l border-rule-strong pl-5">
            {item.summary && <p className="text-step-0 text-ink">{item.summary}</p>}
            <p className="mt-3 text-step--1 text-ink-muted">{copy.externalLead}</p>
            <a
              href={item.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-1.5 rounded-sheet border border-rule-strong px-4 py-2.5 font-ui text-step--1 font-medium uppercase tracking-[0.06em] text-ink transition-colors duration-fast ease-sheet hover:bg-apricot hover:text-navy-deep"
            >
              <ExternalLink size={15} aria-hidden="true" />
              {copy.externalCta}
            </a>
          </div>
        ) : html ? (
          // Shadow root, as everywhere else the hub renders authored HTML: the Unlayer export's
          // own <style> blocks are unscoped and would otherwise bleed into the hub chrome.
          <ShadowContent html={html} />
        ) : (
          <p className="text-step-0 text-ink-muted">{copy.empty}</p>
        )}
      </div>

      {item && (
        <RelatedItems
          kind={kind}
          currentId={item.id}
          categoryId={item.categoryId}
          categoryTitle={item.categoryTitle}
        />
      )}
    </article>
  );
};

export default HubItemPage;
